import { useState, useEffect, useMemo, useCallback } from 'react';

import type { ComboboxOption, ComboboxOptionGroup, ComboboxSearchConfig } from '../combobox.types';
import { fuzzyMatch, flattenOptions } from '../combobox.utils';

/**
 * Hook for managing search functionality in Combobox
 *
 * Features:
 * - Debounced search with configurable delay
 * - Fuzzy matching with threshold
 * - Minimum character threshold
 * - Case sensitivity control
 * - Group preservation in filtered results
 *
 * @example
 * ```tsx
 * const { query, setQuery, filteredOptions, isSearching } = useComboboxSearch({
 *   options,
 *   searchConfig: {
 *     enabled: true,
 *     debounce: 300,
 *     minCharacters: 2,
 *     fuzzyThreshold: 0.6,
 *   },
 * });
 * ```
 */

export interface UseComboboxSearchProps<TValue = string> {
  /** Array of options or option groups to search through */
  options: ComboboxOption<TValue>[] | ComboboxOptionGroup<TValue>[];

  /** Search configuration */
  searchConfig?: boolean | ComboboxSearchConfig<TValue>;

  /** External search query (for controlled search) */
  value?: string;

  /** Callback when search query changes */
  onChange?: (query: string) => void;
}

export interface UseComboboxSearchReturn<TValue = string> {
  /** Current search query */
  query: string;

  /** Debounced search query */
  debouncedQuery: string;

  /** Update search query */
  setQuery: (query: string) => void;

  /** Clear search query */
  clearQuery: () => void;

  /** Filtered options based on search query */
  filteredOptions: ComboboxOption<TValue>[] | ComboboxOptionGroup<TValue>[];

  /** Whether search is enabled */
  isSearchEnabled: boolean;

  /** Whether search is currently debouncing */
  isSearching: boolean;

  /** Whether minimum character threshold is met */
  meetsMinCharacters: boolean;
}

export function useComboboxSearch<TValue = string>({
  options,
  searchConfig = false,
  value: controlledValue,
  onChange: controlledOnChange,
}: UseComboboxSearchProps<TValue>): UseComboboxSearchReturn<TValue> {
  // Parse search configuration
  const config: ComboboxSearchConfig<TValue> | null = useMemo(() => {
    if (searchConfig === false) return null;
    if (searchConfig === true) return { enabled: true };
    return searchConfig;
  }, [searchConfig]);

  const isSearchEnabled = config?.enabled ?? false;
  const debounceMs = config?.debounce ?? 300;
  const minCharacters = config?.minCharacters ?? 0;
  const caseSensitive = config?.caseSensitive ?? false;
  const fuzzyThreshold = config?.fuzzyThreshold ?? 0.6;

  // Internal search query state (for uncontrolled mode)
  const [internalQuery, setInternalQuery] = useState('');

  // Use controlled value if provided, otherwise use internal state
  const query = controlledValue !== undefined ? controlledValue : internalQuery;

  // Debounced query state
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce logic
  useEffect(() => {
    if (!isSearchEnabled) {
      setDebouncedQuery('');
      setIsSearching(false);
      return;
    }

    if (query === debouncedQuery) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query);
      setIsSearching(false);
      config?.onSearch?.(query, flattenOptions(options));
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [query, debouncedQuery, debounceMs, isSearchEnabled, config, options]);

  // Update query handler
  const setQuery = useCallback(
    (newQuery: string) => {
      if (controlledOnChange) {
        controlledOnChange(newQuery);
      } else {
        setInternalQuery(newQuery);
      }
    },
    [controlledOnChange]
  );

  // Clear query handler
  const clearQuery = useCallback(() => {
    setQuery('');
  }, [setQuery]);

  // Check if minimum character threshold is met
  const meetsMinCharacters = useMemo(() => {
    if (!isSearchEnabled || minCharacters === 0) return true;
    return query.length >= minCharacters;
  }, [isSearchEnabled, minCharacters, query]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    // If search is disabled, return all options
    if (!isSearchEnabled) return options;

    // If query is empty or doesn't meet minimum characters, return all options
    if (!debouncedQuery || !meetsMinCharacters) return options;

    // Normalize query for case-insensitive search
    const normalizedQuery = caseSensitive ? debouncedQuery : debouncedQuery.toLowerCase();

    // Helper function to match option against query
    const matchesQuery = (option: ComboboxOption<TValue>): boolean => {
      const searchableText = caseSensitive
        ? `${option.label} ${option.description || ''}`
        : `${option.label} ${option.description || ''}`.toLowerCase();

      return fuzzyMatch(searchableText, normalizedQuery, fuzzyThreshold);
    };

    // Check if options are grouped
    const isGrouped = options.length > 0 && 'options' in options[0];

    if (isGrouped) {
      // Filter grouped options
      const groups = options as ComboboxOptionGroup<TValue>[];
      const filteredGroups = groups
        .map((group) => ({
          ...group,
          options: group.options.filter(matchesQuery),
        }))
        .filter((group) => group.options.length > 0);

      return filteredGroups;
    } else {
      // Filter flat options
      const flatOptions = options as ComboboxOption<TValue>[];
      return flatOptions.filter(matchesQuery);
    }
  }, [options, debouncedQuery, isSearchEnabled, meetsMinCharacters, caseSensitive, fuzzyThreshold]);

  return {
    query,
    debouncedQuery,
    setQuery,
    clearQuery,
    filteredOptions,
    isSearchEnabled,
    isSearching,
    meetsMinCharacters,
  };
}
